import { Formik, FormikValues } from 'formik';
import React, { useContext } from 'react';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { Components } from './styled';
import ProjectTextField from '../../inputs/ProjectTextField';
import { ToastContext } from '../../../providers/ToastProvider/context';
import useModalState from '../../../hooks/useModalState';
import { isProjectError } from '../../../utils/errors';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { Network } from '../../../api/types/network';
import { MinimalNetworkSchema } from '../../../api/network/parser';
import { CreateNetworkDto } from '../../../api/typeDtos/network';
import { createNetwork, updateNetwork } from '../../../api/network';

const initialValues = {
  name: '',
  networkId: '',
};

const ModalBody = ({
  modalState,
  onSave,
  onCancel,
}: {
  modalState?: Network;
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const networkInitialValues = {
    name: modalState?.name || '',
    networkId: modalState?.networkId || '',
  };

  const _onSubmit = async (values: FormikValues) => {
    await onSave?.(values);
  };

  return (
    <Formik
      initialValues={modalState ? networkInitialValues : initialValues}
      validationSchema={MinimalNetworkSchema}
      onSubmit={_onSubmit}>
      {({ values, handleChange, errors, touched }) => {
        return (
          <Components.Form>
            <Components.ModalBodyContainer>
              <Components.FieldsWrapper>
                <Components.FieldStartAdornment variant={'minimal'} />
                <Components.FieldsContent>
                  <Components.FieldWrapper>
                    <Components.FieldLabel>Name</Components.FieldLabel>
                    <ProjectTextField
                      value={values?.name}
                      onChangeText={handleChange}
                      name={'name'}
                      errorMessage={errors.name && touched.name ? errors?.name : undefined}
                      placeholder={'Enter network name'}
                    />
                  </Components.FieldWrapper>
                  <Components.FieldWrapper>
                    <Components.FieldLabel>Network ID</Components.FieldLabel>
                    <ProjectTextField
                      value={values?.networkId}
                      onChangeText={handleChange}
                      name={'networkId'}
                      errorMessage={errors.networkId && touched.networkId ? errors?.networkId : undefined}
                      placeholder={'Enter network ID'}
                    />
                  </Components.FieldWrapper>
                </Components.FieldsContent>
              </Components.FieldsWrapper>
            </Components.ModalBodyContainer>
            <Components.ModalButtonsContainer>
              <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
              <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
            </Components.ModalButtonsContainer>
          </Components.Form>
        );
      }}
    </Formik>
  );
};

const CreateOrEditNetworkModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  refreshCallback,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { network?: Network };
  refreshCallback?: () => Promise<void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const confirmationModal = useModalState<boolean>();

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const data: CreateNetworkDto = {
      name: values?.name,
      networkId: values?.networkId,
    };

    setIsLoading(true);
    const result =
      modalState && modalState?.network?.id
        ? await updateNetwork({ ...data, id: modalState?.network?.id })
        : await createNetwork(data);
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      modalState && modalState?.network?.id
        ? showSuccessToast('The changes were saved successfully')
        : showSuccessToast('You successfully added a network');
      onModalClose?.(true);
      refreshCallback?.().then();
    }
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={<ModalBody modalState={modalState?.network} onSave={_onSave} onCancel={_onModalClose} />}
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={modalState?.network ? `Edit network` : 'Create new network'}
        titleSuffix={modalState?.network ? `(ID: ${modalState?.network?.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};

export default CreateOrEditNetworkModal;
